import React, { useState, useMemo, useEffect } from "react";
import "../styles/folio-form.css";

/** Path for unpaid-dividend lookup POST; base host comes from REACT_APP_API_BASE_URL. */
// Backend expects REACT_APP_API_BASE_URL to already include …/common-api/UnpaidDividend — only append /Data here.
// Legacy full path (do not uncomment unless API contract changes again):
// const UNPAID_DIVIDEND_DATA_PATH = "/common-api/UnpaidDividend/Data";
const UNPAID_DIVIDEND_DATA_PATH = "UnpaidDividend/indetails";


function buildServicesDataUrl(baseFromEnv) {
  const base = (baseFromEnv || "").trim().replace(/\/+$/, "");
  if (!base) return "";
  const path = UNPAID_DIVIDEND_DATA_PATH.startsWith("/")
    ? UNPAID_DIVIDEND_DATA_PATH
    : `/${UNPAID_DIVIDEND_DATA_PATH}`;
  return `${base}${path}`;
}

export default function FolioForm() {
  const [folio, setFolio] = useState("");
  // const [userName, setUserName] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { amount, investorName, respCode, respMsg }
  const [error, setError] = useState("");

  /**
   * Resolve API URL:
   * - Production / hosted: REACT_APP_API_BASE_URL + UNPAID_DIVIDEND_DATA_PATH.
   * - Local dev with empty base: relative path so package.json `proxy` can apply.
   */
  const apiUrl = useMemo(() => {
    const base = (process.env.REACT_APP_API_BASE_URL || "").trim();

    const isLocalhost =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1" ||
        window.location.hostname === "0.0.0.0");

    if (isLocalhost && !base) {
      return UNPAID_DIVIDEND_DATA_PATH;
    }

    return buildServicesDataUrl(base);
  }, []);

  /** Anti Clickjacking Protection */
  // useEffect(() => {
  //   let isFramed = false;

  //   try {
  //     isFramed = window.top !== window.self;
  //   } catch (e) {
  //     isFramed = true;
  //   }

  //   // Normal browser access
  //   if (!isFramed) return;

  //   // Allow Microsoft Teams
  //   const isRunningInTeams =
  //     window.name === "embedded-page-container" ||
  //     navigator.userAgent?.toLowerCase().includes("teams") ||
  //     document.referrer?.includes("teams.microsoft.com") ||
  //     document.referrer?.includes("microsoft.com");

  //   if (isRunningInTeams) {
  //     console.log("✅ Running inside Microsoft Teams - iframe allowed");
  //     return;
  //   }

  //   // Block unauthorized iframe
  //   document.body.innerHTML = `
  //   <div style="
  //     display:flex;
  //     align-items:center;
  //     justify-content:center;
  //     height:100vh;
  //     padding:20px;
  //     background:#fff;
  //     font-family:Arial,sans-serif;
  //     text-align:center;
  //   ">
  //     <div>
  //       <h2>This page cannot be displayed inside an iframe.</h2>
  //       <p style="color:#555;">
  //         For security reasons, open it in a new window or contact the administrator.
  //       </p>
  //     </div>
  //   </div>
  // `;

  //   throw new Error("Framed: aborting app load");
  // }, []);

  const envProblem =
    apiUrl.trim() === ""
      ? "Missing API URL. Set REACT_APP_API_BASE_URL (host only, e.g. https://eportalqa.tatatechnologies.com) in .env.local.development for local dev, or in the CI .env template for builds."
      : null;

  // const isSubmitDisabled =
  //   loading || !folio.trim() || userName.trim().length < 4 || !!envProblem;

  const isSubmitDisabled =
    loading ||
    !folio.trim() ||
    !firstName.trim() ||
    // !lastName.trim() ||
    !!envProblem;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitDisabled) return;

    setLoading(true);
    setError("");
    setResult(null);

    try {
      // const payload = { folioNo: folio.trim(), userName: userName.trim() };
      const payload = {
        folioNo: folio.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim()
      };

      console.log("API url is ==>", apiUrl);

      // ✅ Build auth string safely
      const username = process.env.REACT_APP_VALID_USER_NAME;
      const password = process.env.REACT_APP_VALID_PASSWORD;

      const text = `${username}:${password}`;

      // console.log("user details :", text);

      // ✅ UTF-8 + Base64 (same as RN)
      const authstr = btoa(unescape(encodeURIComponent(text)));


      // console.log("BASE url :", apiUrl);


      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Authorization": `Basic ${authstr}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });


      if (!res.ok) {
        throw new Error(`Network error: ${res.status}`);
      }

      const data = await res.json();
      setResult({
        amount: data?.amount ?? "",
        investorName: data?.investorName ?? "",
        respCode: data?.respCode ?? "",
      });
    } catch (err) {
      setError(err?.message || "Something went wrong while fetching the data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="folio-form-compact">
      <div className="canvas">
        <form className="form" onSubmit={handleSubmit} noValidate>
          {/* Field 1: Folio */}
          <div className="field">
            {/* Caption: edge-to-edge + hard-left */}
            <div className="full-bleed-row left-force">
              <label className="label dm-sans" htmlFor="folio">
                Enter Your Folio Number<span className="req"> *</span>
              </label>
            </div>

            {/* Underline row: full-bleed; typed text is blue */}
            <div className="full-bleed-row left-force">
              <input
                id="folio"
                name="folio"
                className="input edge dm-sans input-blue"
                type="text"
                value={folio}
                onChange={(e) => setFolio(e.target.value)}
                autoComplete="off"
                inputMode="text"
                aria-required="true"
                aria-invalid={folio.trim() === "" ? "true" : "false"}
              />
            </div>
          </div>

          {/* First Name + Last Name */}
          <div className="field">
            <div
              style={{
                display: "flex",
                gap: "30px",
                width: "100%",
              }}
            >
              {/* First Name */}
              <div style={{ width: "300px" }}>

                <div className="full-bleed-row left-force">
                  <label className="label dm-sans" htmlFor="firstName">
                    First Name<span className="req"> *</span>
                  </label>
                </div>

                <div className="full-bleed-row left-force">
                  <input
                    id="firstName"
                    name="firstName"
                    className="input edge dm-sans input-blue"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="off"
                    aria-required="true"
                    aria-invalid={!firstName.trim()}
                  />
                </div>
              </div>

              {/* Last Name */}
              <div style={{ width: "300px" }}>
                <div className="full-bleed-row left-force">
                  <label className="label dm-sans" htmlFor="lastName">
                    Last Name
                    {/* Last Name <span className="req"> *</span> */}
                  </label>
                </div>

                <div className="full-bleed-row left-force">
                  <input
                    id="lastName"
                    name="lastName"
                    className="input edge dm-sans input-blue"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="off"
                    aria-required="true"
                    aria-invalid={!lastName.trim()}
                  />
                </div>
              </div>
            </div>

            <div className="row-between full-bleed-row left-force align-ends">
              <button
                type="submit"
                className="link-btn dm-sans"
                disabled={isSubmitDisabled}
                aria-disabled={isSubmitDisabled}
                title={envProblem || ""}
              >
                {loading ? "Submitting…" : "Submit →"}
              </button>
            </div>
          </div>


        </form>

        {/* Results / Messages */}
        <div className="result-area">
          {error && <div className="error-text dm-sans">{error}</div>}

          {result && (
            <>
              {/* Section title: full-bleed + hard-left */}
              <div className="section-title full-bleed-row no-padding dm-sans left-force">
                Unpaid Dividend Information
              </div>

              {/* Full-bleed table */}
              <div className="table-wrap full-bleed-row left-force">
                <table className="result-table flat dm-sans" role="table">
                  <thead>
                    <tr>
                      <th scope="col">Investor Name</th>
                      <th scope="col">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="input-blue">
                        {result.investorName || "—"}
                      </td>
                      <td className="input-blue">{result.amount || "—"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}